import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SpotlightDisplay = "advertised" | "comparison" | "gap";

interface SlotConfig {
  spotlightName: string | null;
  display: SpotlightDisplay;
}

/**
 * Fetch the spotlight name and display mode assigned to a given page slot.
 */
export function useSlotSpotlight(pageSlug: string, slotId: string) {
  return useQuery({
    queryKey: ["spotlight-slot", pageSlug, slotId],
    queryFn: async (): Promise<SlotConfig> => {
      const { data, error } = await supabase
        .from("spotlight_slots" as any)
        .select("spotlight_name, display")
        .eq("page_slug", pageSlug)
        .eq("slot_id", slotId)
        .maybeSingle();

      if (error) throw error;
      return {
        spotlightName: (data as any)?.spotlight_name ?? null,
        display: ((data as any)?.display as SpotlightDisplay) ?? "advertised",
      };
    },
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Assign (upsert) a spotlight to a page slot.
 */
export function useAssignSlotSpotlight() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pageSlug,
      slotId,
      spotlightName,
      display = "advertised",
    }: {
      pageSlug: string;
      slotId: string;
      spotlightName: string;
      display?: SpotlightDisplay;
    }) => {
      const { data: existing } = await supabase
        .from("spotlight_slots" as any)
        .select("id")
        .eq("page_slug", pageSlug)
        .eq("slot_id", slotId)
        .maybeSingle();

      if ((existing as any)?.id) {
        const { error } = await supabase
          .from("spotlight_slots" as any)
          .update({ spotlight_name: spotlightName, display, updated_at: new Date().toISOString() } as any)
          .eq("id", (existing as any).id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("spotlight_slots" as any)
          .insert({ page_slug: pageSlug, slot_id: slotId, spotlight_name: spotlightName, display } as any);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["spotlight-slot", vars.pageSlug, vars.slotId] });
    },
  });
}
